import {
  AlertCircle,
  Bell,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  MessageSquare,
  Package,
  ShoppingBag,
  ShoppingCart,
  Store,
  TrendingUp,
  User,
  Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { NotificationBadge } from "@/features/notifications";
import { useTicketsUnreadCount } from "@/features/tickets";
import { useDropdownHover } from "@/shared/lib/use-dropdown-hover";
import { ROUTES } from "@/shared/model/routes";
import { Badge } from "@/shared/ui/kit/badge";
import { Button } from "@/shared/ui/kit/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/kit/dropdown-menu";
import { Image } from "@/shared/ui/kit/image";
import { Skeleton } from "@/shared/ui/kit/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/kit/tooltip";

import type { UseNavigationModel } from "../model";

import {
  NavUserBalanceMinimal,
  NavStoreBalanceMinimal,
} from "./nav-balance-breakdown";

type NavActionsProps = {
  model: UseNavigationModel;
  onCartOpen: () => void;
};

export function NavActions({ model, onCartOpen }: NavActionsProps) {
  if (model.isPending) {
    return <NavActionsSkeleton />;
  }

  if (model.isAuthenticated) {
    return <NavActionsAuthenticated model={model} onCartOpen={onCartOpen} />;
  }

  return <NavActionsGuest />;
}

function NavActionsSkeleton() {
  return (
    <div className="flex items-center gap-1">
      <Skeleton className="h-9 w-9 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-md" />
    </div>
  );
}

type NavActionsAuthenticatedProps = {
  model: UseNavigationModel;
  onCartOpen: () => void;
};

function NavActionsAuthenticated({
  model,
  onCartOpen,
}: NavActionsAuthenticatedProps) {
  const { t } = useTranslation();
  const cartItemsCount = model.cartItemCount;
  const ticketsUnread = useTicketsUnreadCount();
  const avatarUrl = `https://api.dicebear.com/9.x/identicon/svg?seed=${model.username}`;
  const storeDropdown = useDropdownHover();
  const userDropdown = useDropdownHover();

  return (
    <>
      {/* Cart */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={onCartOpen}
            aria-label={t("cart.title", "Корзина")}
          >
            <ShoppingCart className="w-5 h-5" />
            {cartItemsCount > 0 && (
              <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 text-[10px] rounded-full flex items-center justify-center">
                {cartItemsCount > 99 ? "99+" : cartItemsCount}
              </Badge>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t("nav.cart")}</TooltipContent>
      </Tooltip>

      {/* Notifications */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={t("nav.notifications")}
            onClick={() => model.setIsNotificationsOpen(true)}
          >
            <Bell className="w-5 h-5" />
            <NotificationBadge
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 text-[10px] rounded-full flex items-center justify-center"
              showCount
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t("nav.notifications")}</TooltipContent>
      </Tooltip>

      {/* Support */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" asChild>
            <Link to={ROUTES.TICKETS}>
              <MessageCircle className="w-5 h-5" />
              {ticketsUnread > 0 && (
                <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 text-[10px] rounded-full flex items-center justify-center">
                  {ticketsUnread > 99 ? "99+" : ticketsUnread}
                </Badge>
              )}
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t("nav.support")}</TooltipContent>
      </Tooltip>

      {/* Store - only for sellers */}
      {model.role === "seller" && (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu
              modal={false}
              open={storeDropdown.open}
              onOpenChange={storeDropdown.setOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onMouseEnter={storeDropdown.handleMouseEnter}
                  onMouseLeave={storeDropdown.handleMouseLeave}
                >
                  <Store className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56"
                onMouseEnter={storeDropdown.handleMouseEnter}
                onMouseLeave={storeDropdown.handleMouseLeave}
              >
                <DropdownMenuLabel className="flex items-center gap-3">
                  {model.store?.avatar_url ? (
                    <Image
                      src={model.store.avatar_url}
                      alt={model.store.name}
                      className="h-8 w-8 rounded-lg bg-muted object-contain"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                      <Store className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">
                      {model.store?.name || t("nav.myStore")}
                    </span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {t("nav.store")}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* Store Balance */}
                {model.storeBalance && (
                  <NavStoreBalanceMinimal
                    storeBalance={model.storeBalance}
                    onWithdraw={model.openWithdrawDialog}
                  />
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.STORE_DASHBOARD}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {t("nav.storeDashboard")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.STORE_PRODUCTS}>
                    <Package className="mr-2 h-4 w-4" />
                    {t("nav.products")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.SALES}>
                    <TrendingUp className="mr-2 h-4 w-4" />
                    {t("nav.sales")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.WITHDRAWALS}>
                    <Wallet className="mr-2 h-4 w-4" />
                    {t("nav.withdrawals")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.STORE_REVIEWS}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t("nav.reviews")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.STORE_DISPUTES}>
                    <AlertCircle className="mr-2 h-4 w-4" />
                    {t("nav.disputes")}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t("nav.myStore")}</TooltipContent>
        </Tooltip>
      )}

      {/* Account */}
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenu
            modal={false}
            open={userDropdown.open}
            onOpenChange={userDropdown.setOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("nav.account")}
                onMouseEnter={userDropdown.handleMouseEnter}
                onMouseLeave={userDropdown.handleMouseLeave}
              >
                <User className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              onMouseEnter={userDropdown.handleMouseEnter}
              onMouseLeave={userDropdown.handleMouseLeave}
            >
              <DropdownMenuLabel className="flex items-center gap-3">
                <Image
                  src={avatarUrl}
                  alt={`${model.username} avatar`}
                  className="h-8 w-8 rounded-lg bg-muted"
                />
                <div className="flex flex-col min-w-0">
                  <span className="truncate">{model.username}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {model.role === "seller" ? t("nav.seller") : t("nav.buyer")}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* Personal Balance */}
              {model.balance && (
                <NavUserBalanceMinimal
                  balance={model.balance}
                  onTopUp={model.openDepositDialog}
                />
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={ROUTES.DASHBOARD}>
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {t("nav.profile")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.PURCHASES}>
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  {t("nav.purchases")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.DEPOSITS}>
                  <Wallet className="mr-2 h-4 w-4" />
                  {t("nav.deposits")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.DISPUTES}>
                  <AlertCircle className="mr-2 h-4 w-4" />
                  {t("nav.disputes")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={model.logout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t("nav.account")}</TooltipContent>
      </Tooltip>
    </>
  );
}

function NavActionsGuest() {
  const { t } = useTranslation();

  return (
    <>
      <Button variant="ghost" size="sm" asChild>
        <Link to={ROUTES.LOGIN}>{t("nav.login")}</Link>
      </Button>
      <Button variant="default" size="sm" asChild>
        <Link to={ROUTES.REGISTER}>{t("nav.register")}</Link>
      </Button>
    </>
  );
}
